import React, { useState } from 'react';
import useAuth from '../hooks/useAuth';
import { useMessageAlert } from './common/MessageAlert';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../utils/colors';
import { useTranslation } from 'react-i18next';

const ForgotPasswordForm = ({ onBackToSignIn }) => {
  const { t } = useTranslation();
  const { requestPasswordReset } = useAuth();
  const { showError, showSuccess } = useMessageAlert();
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotError(t('forgotPassword.errors.emailRequired'));
      return;
    }
    
    // Simple email validation pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError(t('forgotPassword.errors.invalidEmail'));
      return;
    }

    setForgotLoading(true);
    setForgotError('');

    try {
      const result = await requestPasswordReset(forgotEmail);
      if (result.success) {
        showSuccess(result.message || t('forgotPassword.messages.resetLinkSent'));
        onBackToSignIn();
      } else {
        showError(result.error || t('forgotPassword.messages.resetLinkFailed'));
      }
    } catch (err) {
      console.error('Password reset submit error:', err);
      showError(t('common.messages.unexpectedError'));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <form onSubmit={handleForgotSubmit} className="space-y-6">
      {/* Email Field */}
      <div className="space-y-2">
        <label 
          htmlFor="forgotEmail" 
          className="block text-sm font-semibold"
          style={{ color: UI_COLORS.FORMS.LABEL }}
        >
          Email Address
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              style={{ color: UI_COLORS.TEXT.MUTED }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <input
            type="email"
            id="forgotEmail"
            name="forgotEmail"
            value={forgotEmail}
            onChange={(e) => {
              setForgotEmail(e.target.value);
              if (forgotError) setForgotError('');
            }}
            className="w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
            style={{
              backgroundColor: UI_COLORS.FORMS.INPUT.background,
              borderColor: forgotError ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
              color: UI_COLORS.FORMS.INPUT.text,
              ...(forgotError && {
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
              })
            }}
            placeholder={t('forgotPassword.placeholders.email')}
            required
          />
        </div>
        {forgotError && (
          <p 
            className="text-sm font-medium flex items-center gap-1"
            style={{ color: UI_COLORS.STATUS.ERROR }}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {forgotError}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <button
          type="submit"
          disabled={forgotLoading}
          className="w-full py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
          style={{
            background: forgotLoading 
              ? UI_COLORS.BUTTONS.PRIMARY.disabled 
              : GRADIENTS.PRIMARY,
            color: UI_COLORS.BUTTONS.PRIMARY.text,
            boxShadow: `0 4px 14px 0 ${UI_COLORS.SHADOWS.COLORED}`
          }}
          onMouseEnter={(e) => {
            if (!forgotLoading) {
              e.target.style.background = GRADIENTS.WARM;
            }
          }}
          onMouseLeave={(e) => {
            if (!forgotLoading) {
              e.target.style.background = GRADIENTS.PRIMARY;
            }
          }}
        >
          {forgotLoading ? (
            <div className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending link...
            </div>
          ) : (
            t('forgotPassword.buttons.sendResetLink')
          )}
        </button>

        <button
          type="button"
          onClick={onBackToSignIn}
          className="w-full py-3 px-6 rounded-xl font-semibold border-2 transition-all duration-200 text-center"
          style={{
            borderColor: UI_COLORS.BORDERS.LIGHT,
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          Back to Sign In
        </button>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
